<template>
    <label
        :class="[
            'flex',
            {
                'chips-design-item-checked': hasChipsDesign && isChecked,
                chip: hasChipsDesign && !isChecked,
            },
            disabled ? 'text-slate-400' : 'cursor-pointer',
            $attrs.class,
        ]"
    >
        <input
            v-bind="inputAttrs"
            :checked="modelValue === value"
            :disabled="disabled"
            type="radio"
            class="hidden"
            @change="onChange(value)"
        />
        <div
            v-if="!hasChipsDesign"
            class="oval m-0.5 h-5 w-5 shadow-vertical-grey flex items-center justify-center border-2 border-slate-200 rounded-full bg-white flex-shrink-0"
        >
            <div :class="['dot hidden rounded-full', disabled ? 'bg-slate-400' : 'bg-black']"></div>
        </div>
        <span
            :class="[
                {
                    icon: hasChipsDesign && !isChecked,
                    'visible-icon': hasChipsDesign && isChecked,
                    hidden: !hasChipsDesign,
                },
                'h-5 flex items-center justify-center',
            ]"
        >
            <svg
                width="14"
                height="10"
                viewBox="0 0 14 10"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
            >
                <path
                    d="M12.5 1.40259L4.92749 8.59741L1.5 4.59723"
                    stroke="white"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </span>
        <span
            v-if="isSlotUsed"
            :class="[{ 'ml-2': shouldHaveMargin }]"
        >
            <!-- @slot custom html/text label displayed after the radio element -->
            <slot></slot>
        </span>
    </label>
</template>

<script>
import { omit } from 'lodash-es';
import { slotIsUsed } from '@/helpers/dom';

/**
 * Basic radio component which inherits all attributes of default html radio inputs.
 *
 * It is used to handle single selection inputs from a list of options.
 */
export default {
    name: 'InputRadio',
    compatConfig: {
        COMPONENT_V_MODEL: false,
    },
    inheritAttrs: false,
    props: {
        /**
         * The current `value` of the radio(s)
         */
        modelValue: {
            type: undefined,
            default: null,
        },
        /**
         * The value that `modelValue` will be when this radio is checked
         */
        value: {
            type: null,
            default: undefined,
        },
        /**
         * Disable the radio input
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Adding chip design & style to the radio input
         */
        hasChipsDesign: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'change', 'input'],

    computed: {
        isSlotUsed() {
            return slotIsUsed(this.$slots.default);
        },
        isChecked() {
            return this.modelValue !== null && this.modelValue === this.value;
        },
        shouldHaveMargin() {
            return !this.hasChipsDesign || this.isChecked;
        },
        inputAttrs() {
            return omit(this.$attrs, ['class']);
        },
    },

    methods: {
        onChange(val) {
            this.$emit('update:modelValue', val);
            this.$emit('change', val);
            this.$emit('input', val);
        },
    },
};
</script>

<style lang="scss" scoped>
@screen lg {
    .chip:hover {
        @apply bg-blue-200;
    }
}

.dot {
    height: 10px;
    width: 10px;
}

input[type='radio']:checked + .oval .dot {
    display: block;
}

.chips-design-item-checked {
    @apply bg-blue-600 text-white;
}

.icon {
    max-width: 0;
    transition: max-width 150ms ease-out;
    opacity: 0;
}

.visible-icon {
    max-width: 20px;
    transition: max-width 150ms ease-out;
    opacity: 1;
}
</style>
